import { resolveBaseCurrency } from '@/features/order/order-form/lib/instrument-resolution'
import type { SelectedInstrument, SelectedOption } from '@/types/dashboard'
import type { Exchange, Instrument } from '@/types/generated/common/types'

export interface SpreaderInstrumentCandidate {
  exchange: Exchange
  instrument: Instrument
}

/**
 * Related markets are a discovery preference, not a Spreader constraint. The service
 * explicitly supports cross-ticker legs, so keep every candidate and only rank the
 * current spread's base currency first.
 */
export function rankSpreaderInstrumentCandidates(
  candidates: SpreaderInstrumentCandidate[],
  preferredBaseCurrency?: string
): SpreaderInstrumentCandidate[] {
  const preferred = preferredBaseCurrency?.trim().toUpperCase()
  return candidates.toSorted((a, b) => {
    if (preferred) {
      const aPreferred = resolveBaseCurrency([], a.instrument.symbol, a.instrument) === preferred
      const bPreferred = resolveBaseCurrency([], b.instrument.symbol, b.instrument) === preferred
      if (aPreferred !== bPreferred) return aPreferred ? -1 : 1
    }
    return a.instrument.symbol.localeCompare(b.instrument.symbol) || a.exchange - b.exchange
  })
}

type SpreaderSelectionLeg = SelectedInstrument | SelectedOption

function selectionLegIdentity(leg: SpreaderSelectionLeg): string {
  const symbol = 'instrumentName' in leg ? leg.instrumentName : leg.symbol
  return `${leg.accountId ?? ''}\u0000${symbol}`
}

/**
 * The server imposes no same-base or opposite-side rule, and no leg-count cap either —
 * starting from the generic order form only needs two OR MORE effective legs. Identical
 * `(account, symbol)` entries would be coalesced by the server and therefore cannot form
 * a valid quoting/taking pair, so every leg must be unique.
 */
export function canActivateSpreaderSelection(legs: SpreaderSelectionLeg[]): boolean {
  if (legs.length < 2) return false
  const identities = new Set(legs.map(selectionLegIdentity))
  return identities.size === legs.length
}
